import { useQuery } from '@apollo/client';
import { Player } from '@lottiefiles/react-lottie-player';
import { navigate } from 'gatsby';
import { DateTime } from 'luxon';
import { concat, isEmpty, last, map, toLower, uniq } from 'ramda';
import React, { FC, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';
import StyledLink from '@ui/atoms/StyledLink';
import SubheadingText from '@ui/atoms/SubheadingText';

import PastAppointmentCard from '@ui/organisms/PastAppointmentCard/PastAppointmentCard.component';
import ProfileDropdown from '@ui/organisms/ProfileDropdown';
import UpcomingAppointmentsCard from '@ui/organisms/UpcomingAppointmentsCard';

import fullWidth from '@ui/styled/FullWidth';

import { getServiceDisplayName } from '@helpers/Appointments';

import { getAppointmentCta } from '@utils/appointment';
import { interpolate } from '@utils/butter';
import { doesNotExist, exists } from '@utils/general';
import { isBrowser } from '@utils/isBrowser';
import { getFlowName } from '@utils/service-availability';

import { IUnscheduledAppointment } from '@folx-types/member';
import {
	IAppointment,
	IPastAppointments,
	IUpcomingAppointment,
	IUpcomingAppointments,
} from '@folx-types/scheduledAppointments';

import PageLoading from '@assets/lottie/PageLoading.json';

import PAST_APPOINTMENTS_QUERY from '@graphql/queries/PastAppointments.query.gql';

export interface IAppointmentsProps {
	upcomingAppointmentsData?: IUpcomingAppointments;
	loading: boolean;
	hasMembership: boolean;
	pageContent: IAppointmentsData;
	unscheduledAppointments?: IUnscheduledAppointment[];
	serviceTypeDurationMap: Record<string, string>;
	errors?: {
		noInitalAppointmentError?: boolean;
		serviceType: 'HRT_E' | 'HRT_T';
	};
}

export interface IAppointmentsData {
	butterPage: {
		title: string;
		upcomingHeading: string;
		noUpcomingMessage: string;
		noUpcomingFineprint: string;
		noUpcomingFineprintCta: string;
		noInitialError: string;
		bookCta: string;
		pastHeading: string;
	};
}

const FullWidthColumn = fullWidth(FlexColumn);

const StyledPlayer = styled(fullWidth(Player))`
	height: 12rem;
`;

const Appointments: FC<IAppointmentsProps> = ({
	upcomingAppointmentsData,
	loading,
	pageContent,
	hasMembership,
	unscheduledAppointments,
	serviceTypeDurationMap,
	errors,
}) => {
	const [cursor, setCursor] = useState<string | undefined>();
	const [pastAppointments, setPastAppointments] = useState<
		Array<{ cursor: string; node: IAppointment }>
	>([]);

	const {
		butterPage: {
			title,
			upcomingHeading,
			noUpcomingMessage,
			noUpcomingFineprint,
			noUpcomingFineprintCta,
			noInitialError,
			bookCta,
			pastHeading,
		},
	} = pageContent;

	const { loading: loadingPastAppointments, data: pastAppointmentsData } =
		useQuery<IPastAppointments>(PAST_APPOINTMENTS_QUERY, {
			fetchPolicy: 'cache-and-network',
			variables: {
				cursor: cursor,
			},
		});

	useDeepCompareEffect(() => {
		const updatedPastAppointments = uniq(
			concat(pastAppointments, pastAppointmentsData?.pastAppointmentsForCurrentMember?.edges || [])
		);
		setPastAppointments(updatedPastAppointments);
	}, [pastAppointmentsData]);

	const upcomingAppointments =
		upcomingAppointmentsData?.upcomingAppointmentsForCurrentMember.nodes || [];

	const hasMoreAppointments =
		pastAppointmentsData?.pastAppointmentsForCurrentMember?.pageInfo?.hasNextPage;

	const UpcomingAppointments = () => (
		<LoadingWrapper loading={loading} skeleton={<Skeleton height="xxxxxl" fullWidth gap="md" />}>
			<FullWidthColumn gap="md">
				{map((unscheduledAppointment: IUnscheduledAppointment) => {
					const service = getServiceDisplayName(unscheduledAppointment?.appointmentType ?? '');
					console.log(unscheduledAppointment.appointmentType);
					const appointmentLength =
						serviceTypeDurationMap[unscheduledAppointment?.appointmentType ?? ''] === 'brief'
							? 15
							: 30;
					return (
						<UpcomingAppointmentsCard
							key={unscheduledAppointment.cogsworthId}
							serviceName={service}
							appointmentCta={'Reschedule / Cancel'}
							appointmentDuration={`${appointmentLength} min virtual visit`}
							handleClick={() => {
								navigate(`reschedule/${unscheduledAppointment.cogsworthId}`);
							}}
							visitType={unscheduledAppointment?.appointmentType}
						/>
					);
				}, unscheduledAppointments ?? [])}
				{map((upcomingAppointment: IUpcomingAppointment) => {
					const convertedDate = DateTime.fromISO(upcomingAppointment.scheduledDt);
					const convertedHours = convertedDate?.diffNow('hours').hours;
					const localizedTime = convertedDate?.toFormat('h:mm a ZZZZ');

					const appointmentLength = upcomingAppointment?.duration ?? 0;
					const staffName = upcomingAppointment?.staffMember ?? 'Scheduled Clinician';
					const service = getServiceDisplayName(upcomingAppointment?.serviceType ?? '');

					const isWithinAppointmentWindow = Math.abs(convertedHours) <= 1;

					return (
						<UpcomingAppointmentsCard
							key={upcomingAppointment.cogsworthId}
							name={staffName}
							serviceName={service}
							appointmentCta={getAppointmentCta(
								upcomingAppointment.scheduledDt,
								upcomingAppointment.visitType
							)}
							appointmentDate={`${convertedDate.toLocaleString(
								DateTime.DATE_MED
							)} at ${localizedTime}`}
							appointmentDuration={`${appointmentLength} min ${
								upcomingAppointment.visitType === 'irl' ? 'in-person' : 'virtual'
							} visit`}
							handleClick={() => {
								if (!isBrowser) {
									return;
								}
								isWithinAppointmentWindow
									? window.open(upcomingAppointment.visitUrl, '__blank')
									: navigate(`reschedule/${upcomingAppointment.cogsworthId}`);
							}}
							visitType={upcomingAppointment.visitType}
						/>
					);
				})(upcomingAppointments ?? [])}
				{doesNotExist(upcomingAppointments) && doesNotExist(unscheduledAppointments) && (
					<>
						<BasicText>{noUpcomingMessage}</BasicText>
						<BasicText variant="fineprint" color="black" tint="lighten40">
							{noUpcomingFineprint}
							<StyledLink
								to={process.env.GATSBY_ZENDESK_SSO_LINK || ''}
								variant="small"
								color="black">
								{noUpcomingFineprintCta}
							</StyledLink>
						</BasicText>
					</>
				)}
			</FullWidthColumn>
		</LoadingWrapper>
	);

	const PastAppointments = () => (
		<LoadingWrapper loading={loadingPastAppointments && isEmpty(pastAppointments)} skeleton={<></>}>
			<FullWidthColumn gap="md">
				{exists(pastAppointments) && <SubheadingText>{pastHeading}</SubheadingText>}
				{pastAppointments.map((p) => (
					<PastAppointmentCard {...p.node} key={p.node.cogsworthId} />
				))}
				{loadingPastAppointments && <StyledPlayer autoplay loop src={PageLoading} />}
				{hasMoreAppointments && (
					<Button
						label="Load more past appointments"
						variant="secondary"
						onClick={() => {
							const appointmentCursor = last(pastAppointments)?.cursor;
							setCursor(appointmentCursor);
						}}
					/>
				)}
			</FullWidthColumn>
		</LoadingWrapper>
	);

	return (
		<FullWidthColumn gap="lg">
			<FullWidthColumn gap="md">
				<ProfileDropdown />
				<HeadingText>{title}</HeadingText>
				{errors?.noInitalAppointmentError && (
					<BasicText color="red" tint="darken40">
						{interpolate(noInitialError, {
							serviceType: toLower(getFlowName(toLower(errors?.serviceType ?? ''))),
						})}
					</BasicText>
				)}
				<LoadingWrapper
					loading={loading}
					skeleton={<Skeleton fullWidth height="xl" borderRadius="lg" />}>
					<Button
						variant="secondary"
						href={hasMembership ? '/app/member/get_care/' : '/app/member/reactivate_membership/'}
						label={bookCta}
					/>
				</LoadingWrapper>
			</FullWidthColumn>
			<FullWidthColumn gap="md">
				<SubheadingText>{upcomingHeading}</SubheadingText>
				<UpcomingAppointments />
			</FullWidthColumn>
			<PastAppointments />
		</FullWidthColumn>
	);
};

export default Appointments;
